import { useMemo, useState } from "react";

import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";

import {
  DraftingSetupApplyDocument,
  type DraftingSourceEnum,
  ProjectDraftingSetupDocument,
  type ProjectDraftingSetupQuery,
} from "@app_schema";

import { track } from "@application/utilities/track";

import { sourceOptions } from "./drafting_sources";

type Proposal = NonNullable<
  ProjectDraftingSetupQuery["project"]["draftingSetupProposal"]
>;
export type SetupSection = Proposal["sections"][number];

// What he's changed on a proposed section: kept or not, its name, what it reads.
type Choice = { checked: boolean; name: string; sources: DraftingSourceEnum[] };

// A kept section's place in the Report's preview: before block `before` of
// his file, labeled as its place will be once it's added.
export type SetupSlot = {
  block: number;
  before: number;
  name: string;
  label: string;
};

// Kept the same between renders, so what reads them doesn't re-run.
const NONE: [] = [];

// Keyed by its heading's block in the file read, which is its own.
const keyOf = (proposal: Proposal | null | undefined, section: SetupSection) =>
  `${String(proposal?.fileVersion)}:${String(section.block)}`;

const choiceIn = (
  choices: ReadonlyMap<string, Choice>,
  proposal: Proposal | null | undefined,
  section: SetupSection,
): Choice =>
  choices.get(keyOf(proposal, section)) ?? {
    checked: section.checked,
    name: section.name,
    sources: [...section.sources],
  };

// "Intake, Records", as Your sections lists them; "" when it reads nothing.
export const sourceNames = (sources: readonly DraftingSourceEnum[]) =>
  sourceOptions(sources)
    .filter((source) => source.checked)
    .map((source) => source.label)
    .join(", ");

// Easy setup's proposal for the case's Word draft, his choices on it, and
// adding the ones he keeps at once. The server proposes nothing once the case
// has sections; Skip only hides it for now. Read once (his file is parsed for
// it): a stale one is refused on add, and Reload reads it again.
export const useDraftingSetup = (
  projectId: string,
  enabled: boolean,
  // His sections, and the chat's while there is one (see useDraftingSections).
  rechecked: string[],
) => {
  const client = useApolloClient();
  // What a reload or an add asks for again: his sections and the proposal.
  const refreshed = [...rechecked, "ProjectDraftingSetup"];
  const { data, loading } = useQuery(ProjectDraftingSetupDocument, {
    variables: { id: projectId },
    fetchPolicy: "cache-first",
    skip: !enabled,
  });
  const [applyMutation, applyState] = useMutation(DraftingSetupApplyDocument, {
    refetchQueries: refreshed,
    awaitRefetchQueries: true,
  });
  const [choices, setChoices] = useState<ReadonlyMap<string, Choice>>(
    new Map(),
  );
  const [skipped, setSkipped] = useState(false);
  // The section picked in the list or the Report, by its heading's block.
  const [selected, setSelected] = useState<number | null>(null);
  const proposal = data?.project.draftingSetupProposal;

  const choiceOf = (section: SetupSection) =>
    choiceIn(choices, proposal, section);
  const change = (section: SetupSection, change: Partial<Choice>) =>
    setChoices((current) =>
      new Map(current).set(keyOf(proposal, section), {
        ...choiceIn(current, proposal, section),
        ...change,
      }),
    );
  const kept = (proposal?.sections ?? []).filter(
    (section) => choiceOf(section).checked,
  );
  const slots = useMemo(
    () =>
      (proposal?.sections ?? []).flatMap((section): SetupSlot[] => {
        const { checked, name, sources } = choiceIn(choices, proposal, section);
        // A section kept from before his template changed isn't in the list.
        if (!checked || section.kept) return [];
        const names = sourceNames(sources);
        const reads = names ? `From ${names}` : "You write this";
        const shown = name.trim() || section.name;
        return [
          {
            block: section.block,
            before: section.before,
            name: shown,
            label: `${shown} · ${reads}`,
          },
        ];
      }),
    [proposal, choices],
  );

  const apply = async () => {
    if (!proposal) return;
    const applied = await applyMutation({
      variables: {
        id: projectId,
        input: {
          fileVersion: proposal.fileVersion,
          templateVersion: data.project.draftingTemplateVersion,
          sections: kept.map((section) => {
            const { name, sources } = choiceOf(section);
            return {
              before: section.before,
              name: name.trim() || section.name,
              matchKey: section.matchKey,
              sources,
            };
          }),
        },
      },
    }).catch(() => undefined);
    if (applied) {
      track("drafting_setup_applied", {
        project_id: projectId,
        section_count: kept.length,
      });
    }
  };

  const shown = enabled && !skipped;
  const finding = shown && loading && !data;
  return {
    proposal: shown ? proposal : null,
    // His file is still being read for its sections.
    finding,
    // The review is up in place of Your sections, so nothing is drafted yet.
    showing: finding || Boolean(shown && proposal),
    choiceOf,
    change,
    selected,
    select: setSelected,
    slots: shown ? slots : NONE,
    keptCount: kept.length,
    apply,
    applying: applyState.loading,
    error: applyState.error?.message,
    // His file or sections changed: read them again, and start his choices over.
    reload: () => {
      setChoices(new Map());
      setSelected(null);
      applyState.reset();
      void client.refetchQueries({ include: refreshed });
    },
    skip: () => setSkipped(true),
  } as const;
};
